import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerTokenService } from '../../services/server-token.service';
import {
  FacilityGroup,
  FacilityGroupFacility,
  FacilityGroupsLoadData,
  CreateGroupInput,
  UpdateGroupInput,
} from './facility-groups.interface';

@Injectable({
  providedIn: 'root',
})
export class FacilityGroupsService {
  constructor(private server: ServerTokenService) {}

  loadAll(): Observable<FacilityGroupsLoadData> {
    return forkJoin({
      groups: this.server
        .getWithToken<{ groups: FacilityGroup[] }>('snfs/admin/facility-groups')
        .pipe(map(res => res.groups || [])),
      facilities: this.server
        .getWithToken<{ facilities: FacilityGroupFacility[] }>(
          'snfs/admin/facility-groups/facilities/list'
        )
        .pipe(map(res => res.facilities || [])),
      applyFacilityGroupsInCensus: this.server
        .getWithToken<{ groups: any; applyFacilityGroupsInCensus: boolean }>(
          'snfs/admin/facility-groups'
        )
        .pipe(map(res => res.applyFacilityGroupsInCensus || false)),
    }).pipe(map(data => ({ ...data } as FacilityGroupsLoadData)));
  }

  createGroup(input: CreateGroupInput): Observable<FacilityGroup> {
    return this.server.postWithToken<FacilityGroup>(
      'snfs/admin/facility-groups',
      input
    );
  }

  updateGroup(groupId: string, input: UpdateGroupInput): Observable<FacilityGroup> {
    return this.server.patchWithToken<FacilityGroup>(
      `snfs/admin/facility-groups/${groupId}`,
      input
    );
  }

  deleteGroup(groupId: string): Observable<{ success: boolean }> {
    return this.server.deleteWithToken<{ success: boolean }>(
      `snfs/admin/facility-groups/${groupId}`
    );
  }

  setFacilityAssignment(
    facilityId: number,
    facilityGroupId: string | null
  ): Observable<any> {
    return this.server.patchWithToken(
      `snfs/admin/facility-groups/facilities/${facilityId}/assign`,
      { facilityGroupId }
    );
  }

  bulkSetAssignments(
    facilityIds: number[],
    facilityGroupId: string | null
  ): Observable<{ success: boolean }> {
    return this.server.postWithToken<{ success: boolean }>(
      'snfs/admin/facility-groups/facilities/bulk-assign',
      { facilityIds, facilityGroupId }
    );
  }

  bulkUnassign(facilityIds: number[]): Observable<{ success: boolean }> {
    return this.server.postWithToken<{ success: boolean }>(
      'snfs/admin/facility-groups/facilities/bulk-unassign',
      { facilityIds }
    );
  }

  toggleCensusApply(
    applyFacilityGroupsInCensus: boolean
  ): Observable<{ applyFacilityGroupsInCensus: boolean }> {
    return this.server.patchWithToken(
      'snfs/admin/facility-groups/settings/census',
      { applyFacilityGroupsInCensus }
    );
  }
}
